import React, { useState, useEffect } from 'react';
import { Sparkles, ArrowRight, ExternalLink } from 'lucide-react';

/**
 * The ad itself, in the exact shape each space renders it.
 *
 * Pulled out of SpotlightSlot (session 62c) so the admin preview is the *same markup* members
 * see — a preview built from a lookalike would drift the first time either side changed.
 * Also where a broken image link is handled: a bad URL now falls back to a text-only ad instead
 * of leaving a broken-image box in the middle of the Marketplace.
 */
export const THEMES = {
  dark: {
    border: 'border-amber-400/25 hover:border-amber-400/50',
    bgRow: 'bg-gradient-to-r from-amber-500/[0.07] via-white/[0.02] to-transparent',
    bgCol: 'bg-gradient-to-b from-amber-500/[0.08] to-white/[0.02]',
    title: 'text-white',
    body: 'text-white/60',
    cta: 'text-amber-300',
    badge: 'text-amber-300 bg-amber-400/15',
  },
  light: {
    border: 'border-amber-400/60 hover:border-amber-500',
    bgRow: 'bg-amber-50',
    bgCol: 'bg-amber-50',
    title: 'text-gray-900',
    body: 'text-gray-600',
    cta: 'text-amber-700',
    badge: 'text-amber-700 bg-amber-100',
  },
};

const same = (s) => s;

export function SpotlightCreative({
  p: ad, variant = 'banner', theme = 'dark', onClick, tx = same, className = '', testId,
  onImageError,
}) {
  const [imgBroken, setImgBroken] = useState(false);
  const t = THEMES[theme] || THEMES.dark;
  // Placeholders so an empty form still previews as a real ad.
  const p = {
    ...ad,
    title: ad?.title || 'Your headline appears here',
    body: ad?.body || 'And your supporting line sits underneath.',
    cta_label: ad?.cta_label || 'Learn more',
  };
  const image = p.image_url && !imgBroken ? p.image_url : null;
  const onImgError = () => { setImgBroken(true); onImageError?.(); };
  // Typing a new link after a broken one must give the image another chance.
  useEffect(() => { setImgBroken(false); }, [ad?.image_url]);

  const Badge = () => (
    <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${t.badge}`}>
      <Sparkles className="w-3 h-3" /> {tx(p.label) || tx('Featured')}
    </span>
  );
  const CtaText = ({ size = 'text-sm' }) => (
    <span className={`inline-flex items-center gap-1 font-medium ${size} ${t.cta} group-hover:gap-2 transition-all`}>
      {tx(p.cta_label) || tx('Learn more')}
      {p.cta_type === 'external' ? <ExternalLink className="w-3.5 h-3.5" /> : <ArrowRight className="w-3.5 h-3.5" />}
    </span>
  );

  if (variant === 'banner') {
    return (
      <button type="button" onClick={onClick}
        className={`group w-full text-left rounded-xl border ${t.border} ${t.bgRow} transition-colors p-4 flex items-center gap-4 ${className}`}
        data-testid={testId}>
        {image && <img src={image} alt={p.title} onError={onImgError}
          className="w-28 h-20 sm:w-40 sm:h-24 object-cover rounded-lg flex-shrink-0" />}
        <div className="min-w-0 flex-1">
          <Badge />
          <h3 className={`font-semibold text-base sm:text-lg mt-1 truncate ${t.title}`}>{tx(p.title)}</h3>
          {p.body && <p className={`text-sm line-clamp-2 ${t.body}`}>{tx(p.body)}</p>}
        </div>
        <span className="hidden sm:block flex-shrink-0"><CtaText /></span>
      </button>
    );
  }

  if (variant === 'card' || variant === 'flank') {
    return (
      <button type="button" onClick={onClick}
        className={`group text-left rounded-xl border ${t.border} ${t.bgCol} transition-colors overflow-hidden flex flex-col ${className}`}
        data-testid={testId}>
        {image && <img src={image} alt={p.title} onError={onImgError} className="w-full h-32 object-cover" />}
        <div className="p-3 flex flex-col gap-1 flex-1">
          <Badge />
          <h3 className={`font-semibold text-sm mt-1 line-clamp-2 ${t.title}`}>{tx(p.title)}</h3>
          {p.body && <p className={`text-xs line-clamp-2 flex-1 ${t.body}`}>{tx(p.body)}</p>}
          <span className="mt-1"><CtaText size="text-xs" /></span>
        </div>
      </button>
    );
  }

  // rail
  return (
    <button type="button" onClick={onClick}
      className={`group flex-shrink-0 w-64 text-left rounded-xl border ${t.border} ${t.bgRow} transition-colors p-3 flex items-center gap-3 ${className}`}
      data-testid={testId}>
      {image && <img src={image} alt={p.title} onError={onImgError}
        className="w-14 h-14 object-cover rounded-lg flex-shrink-0" />}
      <div className="min-w-0">
        <Badge />
        <h3 className={`font-semibold text-sm mt-1 truncate ${t.title}`}>{tx(p.title)}</h3>
        <CtaText size="text-xs" />
      </div>
    </button>
  );
}

export default SpotlightCreative;
